import React, { useState, useCallback, Suspense } from 'react';
import { noop } from 'lodash';
import PropTypes from 'prop-types';
import pathfinder from '@fiverr-private/pathfinder';
import { Button } from '@fiverr-private/button';
import { I18n } from '@fiverr-private/i18n-react';
import {
    FLOW_CONTEXT_TYPES,
    ModalType,
    openFipMigrationModal,
    shouldDisplayMigrationPopup,
} from '@fiverr-private/pro_migration';
import { useSuccessSignCtaEvent, BUSINESS_TRIGGER_CTA } from '@fiverr-private/business_success_sign_modal';
import { clickOnGigCustomOrderEvent } from '../../../utils/biEvents/events';
import { currencyType } from '../../../shared/types';
import { useCustomOrderFormMutation } from './useCustomOrderFormData';

const CustomOrderModal = React.lazy(() =>
    import(/* webpackChunkName: 'CustomOrderModal' */ '@fiverr-private/custom_order/src/entries/CustomOrderModalEntry')
);

const T_PREFIX = 'listings.custom_order_card';

const uploadOptions = {
    objectName: 'message',
    getPolicyUrl: pathfinder('messages_attachments_policy'),
    createPendingAttachmentIdUrl: pathfinder('messages_create_pending_attachment'),
};

const categoriesDataSerializer = (data) =>
    data.dds.categories.options.map((item) => ({
        value: item.val,
        label: item.text,
        subCategories: data.dds.subCategories.options[item.val].map((subItem) => ({
            value: subItem.val,
            label: subItem.text,
            metadata: data.metaData[item.val] || [],
        })),
    }));

export const RequestButton = ({
    sellerUsername,
    sellerDisplayName,
    sellerImage,
    isUserPending,
    openUserActivateModal = noop,
    currency,
    source,
}) => {
    const [initModal, setInitModal] = useState(false);
    const [openModal, setOpenModal] = useState(() => noop);
    const {
        data: formData,
        mutateAsync: fetchFormData,
        isLoading,
        isSuccess,
    } = useCustomOrderFormMutation(sellerUsername);

    /** @param {typeof noop} open */
    const setOpenModalHandle = (open) => setOpenModal(() => open);
    /** @type {() => Promise} */
    const request = useCallback(async () => {
        clickOnGigCustomOrderEvent({ source, sellerUsername });
        openModal();
        if (!isSuccess) {
            await fetchFormData();
        }
        setInitModal(true);
    }, [source, sellerUsername, isSuccess, openModal, fetchFormData]);

    const onRequestClick = useCallback(async () => {
        if (isUserPending) {
            openUserActivateModal();
            return;
        }
        if (shouldDisplayMigrationPopup()) {
            openFipMigrationModal({
                variant: ModalType.FullMigration,
                payload: {
                    source: `${source}.contact`,
                    biEnrichment: {
                        pageElementName: source,
                        trigger: 'contact',
                    },
                    flowContext: {
                        type: FLOW_CONTEXT_TYPES.requestQuote,
                        sellerName: sellerUsername,
                    },
                    redirectCtaData: {
                        type: BUSINESS_TRIGGER_CTA.REQUEST_QUOTE,
                        source: `request_a_quote-${source}`,
                    },
                    onSuccess: request,
                },
                callbackWhenNotMounted: noop,
            });
        } else {
            await request();
        }
    }, [source, isUserPending, sellerUsername, openUserActivateModal, request]);

    useSuccessSignCtaEvent({
        source: `request_a_quote-${source}`,
        type: BUSINESS_TRIGGER_CTA.REQUEST_QUOTE,
        callback: request,
    });

    const onClose = () => {
        setInitModal(false);
        setOpenModalHandle(noop);
    };

    const categoriesData = formData ? categoriesDataSerializer(formData) : [];
    const { persistType, submitPath } = formData || {};

    return (
        <div>
            <Button size="sm" variant="outline" intent="secondary" disabled={isLoading} onClick={onRequestClick}>
                <I18n k={`${T_PREFIX}.request_button`} />
            </Button>
            {initModal && (
                <Suspense fallback={null}>
                    <CustomOrderModal
                        key={sellerUsername}
                        source={source}
                        autoOpen={true}
                        submitPath={submitPath}
                        persistType={persistType}
                        trigger={setOpenModalHandle}
                        categoriesData={categoriesData}
                        currency={currency}
                        seller={{ name: sellerUsername, image: sellerImage, displayName: sellerDisplayName }}
                        uploadOptions={uploadOptions}
                        onClose={onClose}
                    />
                </Suspense>
            )}
        </div>
    );
};

RequestButton.propTypes = {
    sellerUsername: PropTypes.string.isRequired,
    isUserPending: PropTypes.bool,
    openUserActivateModal: PropTypes.func,
    sellerImage: PropTypes.string,
    sellerDisplayName: PropTypes.string,
    currency: currencyType,
    source: PropTypes.string,
};
